/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { toggleClass } from '@grapecity/wijmo';
import { DataMap, CellType, HeadersVisibility } from '@grapecity/wijmo.grid';
import { CommonUtil } from 'utils/CommonUtil';
import { commonYNList } from 'models/common/CommonCode';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  LSmRLgContetntLayout,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { IconButton } from 'components/buttons/IconSVG';
import { CrudCode } from 'models/common/Edit';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import { hasRole } from 'utils/SessionUtil';
import { tb } from 'components/layouts/Table';
import { findEquipmentGroup } from 'apis/common/popup/EquipmentGroup';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { SetupOrgEqcl, SetupOrgEqclCondition } from 'models/setup/SetupOrgEqcl';
import { CommonYN } from 'models/common/Common';
import { findOrgEqcl, saveOrgEqcl } from 'apis/setup/SetupOrgEqcl';

/**
 * @desc 설비셋업관리 > 프로젝트 기준정보 > 조직별 설비군 관리
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.08.20   jshwang12       신규 생성
 * 2026.09.01   jshwang12       권한 설정  (FA 관리자, 시스템관리자)
 */
const SetupOrgEqclPage = () => {
  const gridRef = useRef<any>();
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();
  const [code, setCode] = useState<any>();
  const [condition, setCondition] = useState<SetupOrgEqclCondition>();
  const [selectedOrgTpCd, setSelectedOrgTpCd] = useState<string>();
  const [rowData, setRowData] = useState<SetupOrgEqcl[]>([]);
  const [eqpGroup, setEqpGroup] = useState<EquipmentGroupCondition[]>();
  const [hasAuth, setHasAuth] = useState<boolean>(false);

  const selectedOrgTpCdNm = useMemo(() => {
    return `${
      (code?.prjOrgTpCd || []).find((o) => o.cmnCd === selectedOrgTpCd)?.cmnCdNm || selectedOrgTpCd
    }`;
  }, [code?.prjOrgTpCd, selectedOrgTpCd]);

  const countByOrg = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rowData) {
      if (r.crudKey === CrudCode.DELETE) continue;
      map.set(r.orgTpCd, (map.get(r.orgTpCd) ?? 0) + 1);
    }
    return map;
  }, [rowData]);

  const filterRowData = useMemo(() => {
    return (rowData || []).filter((o) => o.orgTpCd === selectedOrgTpCd);
  }, [rowData, selectedOrgTpCd]);

  useEffect(() => {
    getCommonCodes();
    // [26.09.01] FA 관리자, 시스템관리자만 수정 가능
    if (hasRole('ADM') || hasRole('FA_MANAGER')) {
      setHasAuth(true);
    }
    handleSearch();
  }, []);

  const getCommonCodes = () => {
    Promise.all([
      getCommonCodeNames('PRJ_ORG_TP_CD'),
      getCommonCodeNames('PRJ_SP_DIVS_CD'), // 설치구분 (ex. 운송, 기구 등)
      getCommonCodeNames('ISSU_CRTN_CD'), // 발행기준
      getCommonCodeNames('PSG_CRGE_TP_CD'), // 처리담당자유형
      findEquipmentGroup({ useYn: 'Y' }),
    ]).then(([prjOrgTpCd, prjSpDivsCd, issuCrtnCd, psgCrgeTpCd, eqp]) => {
      setCode({
        prjOrgTpCd: prjOrgTpCd,
        prjSpDivsCd: prjSpDivsCd,
        issuCrtnCd: issuCrtnCd,
        psgCrgeTpCd: psgCrgeTpCd,
      });
      if ((prjOrgTpCd || []).length > 0 && prjOrgTpCd[0]?.cmnCd) {
        setSelectedOrgTpCd(prjOrgTpCd[0]?.cmnCd);
      }
      setEqpGroup(eqp);
    });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        align: 'center',
        isReadOnly: true,
        visible: !hasAuth,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        visible: hasAuth,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'eqclId',
        header: String(t('setup.label.설비군', '설비군')),
        align: 'center',
        minWidth: 50,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(eqpGroup || [], 'equipmentGroup', 'equipmentGroup'),
      },
      {
        binding: 'eqclNm',
        header: String(t('setup.label.설비군명', '설비군명')),
        align: 'center',
        minWidth: 80,
        width: '*',
        isReadOnly: true,
      },
      {
        binding: 'useYn',
        header: String(t('com.label.사용여부', '사용여부')),
        align: 'center',
        minWidth: 50,
        width: 100,
        isRequired: false,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(commonYNList || [], 'cmnCd', 'cmnCd'),
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일시', '수정일시')),
        minWidth: 50,
        width: 160,
        align: 'center',
        isReadOnly: true,
      },
    ];
  }, [hasAuth, eqpGroup]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.headersVisibility = hasAuth
        ? HeadersVisibility.All
        : HeadersVisibility.Column;
    }
  }, [hasAuth]);

  const handleAddRow = useEvent(() => {
    if (!selectedOrgTpCd) {
      openMessageBar({
        type: 'warning',
        content: t('setup.label.조회 후 등록해 주세요.', '조회 후 등록해 주세요'),
      });
      return;
    }
    const newRow = {
      crudKey: CrudCode.CREATE,
      orgTpCd: selectedOrgTpCd,
      eqclId: '',
      eqclNm: '',
      eqpCtgrTp: '',
      useYn: CommonYN.Y,
    } as SetupOrgEqcl;
    const rows = [newRow, ...rowData];
    setRowData(rows);
  });

  const handleDelRow = () => {
    const selectedRows = gridRef.current?.rows?.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        const index = rowData.findIndex((r) => r === row.dataItem);
        return index;
      })
      .filter((index) => index !== -1);

    selectedIds.sort((a, b) => b - a);

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록'),
      t('setup.label.셋업 단계 별 Activity 목록', '셋업 단계 별 Activity 목록')
    );
  };

  const validate = () => {
    const seen = new Map<string, number>();
    const dupKeys = [] as string[];
    (rowData || []).forEach((o) => {
      const key = `${o.orgTpCd}||${o.eqclId}`;
      if (key) {
        const count = seen.get(key) || 0;
        seen.set(key, count + 1);
        if (count === 1) {
          dupKeys.push(key);
        }
      }
    });

    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, idx) => {
        if (_.isEmpty(o.eqclId)) {
          return `${idx + 1} : ${t(
            'setup.label.설비군을 선택해 주세요.',
            '설비군을 선택해 주세요.'
          )}`;
        }
        const key = `${o.orgTpCd}||${o.eqclId}`;
        if (dupKeys.includes(key || '')) {
          return `${idx + 1} : ${t(
            'setup.label.동일 설비군이 중복되었습니다.',
            '동일 설비군이 중복되었습니다.'
          )} (${o.orgTpCd} / ${o.eqclId})`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.join('<br/>') });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!(rowData || []).filter((o) => o.crudKey).length) {
      return;
    }

    if (validate()) {
      const delCount = (rowData || []).filter((o) => o.crudKey === CrudCode.DELETE).length;
      const msg =
        delCount > 0
          ? t('setup.label.삭제 {{cnt}}건이 포함되어 있습니다. 저장하시겠습니까?', {
              defaultValue: '삭제 {{cnt}}건이 포함되어 있습니다. 저장하시겠습니까?',
              cnt: delCount,
            })
          : t('com.label.저장하시겠습니까?', '저장하시겠습니까?');
      openCommonModal({
        modalType: 'confirm',
        content: msg,
        yesCallback: async () => {
          saveOrgEqcl(rowData).then((res) => {
            openMessageBar({
              type: res?.successOrNot === 'Y' ? 'confirm' : 'error',
              content:
                res?.successOrNot === 'Y'
                  ? t('com.label.저장되었습니다.', '저장되었습니다.')
                  : res?.data && typeof res?.data === 'string'
                  ? res.data
                  : t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
            if (res?.successOrNot === 'Y') {
              handleSearch();
            }
          });
        },
      });
    }
  };

  const handleSearch = () => {
    Promise.all([findOrgEqcl()]).then(([rows]) => {
      setRowData(rows);
    });
  };

  const onBeginningEdit = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;

    if (CrudCode.CREATE !== item.crudKey) {
      // 수정 시 설비군 수정불가
      if ('eqclId' === binding) {
        e.cancel = true;
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;
    const oldVal = grid.getCellData(e.row, e.col);
    const newVal = grid.activeEditor?.value;
    if ('eqclId' === binding) {
      const newEqcl = (eqpGroup || []).find((o) => o.equipmentGroup === newVal);
      // 값이 없는 경우 초기화
      if (!newEqcl?.equipmentGroup) {
        item.eqclNm = '';
      }
      if (oldVal !== newEqcl?.equipmentGroup) {
        item.eqclNm = newEqcl?.equipmentGroupName;
      }
    }
  });

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (CrudCode.CREATE !== item.crudKey) {
        // 수정 시 설비군 수정불가
        if ('eqclId' === binding) {
          cell.ariaReadOnly = true;
          toggleClass(cell, 'WijmoSelect', true);
          if (cell.firstElementChild instanceof HTMLButtonElement) {
            cell.firstChild.remove();
          }
        }
      }
    }
  });

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    grid.headersVisibility = hasAuth ? HeadersVisibility.All : HeadersVisibility.Column;
  });

  return (
    <>
      <style>
        {`
          tr.selectable {
            cursor: pointer;
          }
          tr.selected td {
            background: #e2f4f7 !important;
          }
          .cnt-tag {
              display: inline-block;
              min-width: 26px;
              border-radius: 9px;
              padding: 0 7px;
              background: #eef7f9;
              color: #14708a;
              font-size: 11px;
              font-weight: 700;
              text-align: center;
            }
        `}
      </style>
      <LSmRLgContetntLayout>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('setup.label.대상 조직', '대상 조직')}</h3>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{(code?.prjOrgTpCd || []).length.toLocaleString()}</span>
                {t('com.label.명', '명')}
              </span>
            </SubTitleGroup>
          </SubTitleLayout>
          <TableContainer css={tb.tableCol}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('setup.label.조직명', '조직명')}</TableCell>
                  <TableCell style={{ width: 70 }}>{t('setup.label.등록', '등록')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(code?.prjOrgTpCd || []).map((o) => (
                  <TableRow
                    key={o.cmnCd}
                    className={`selectable ${o.cmnCd === selectedOrgTpCd ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedOrgTpCd(o.cmnCd);
                    }}
                  >
                    <TableCell>{o.cmnCdNm}</TableCell>
                    <TableCell>
                      <span className="cnt-tag">{countByOrg.get(o.cmnCd) ?? 0}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div>
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('setup.label.설비군 목록', '설비군 목록')}</h3>
              <span className="total">
                {t('com.label.총', '총')}
                <span>{(filterRowData || []).length.toLocaleString()}</span>
                {t('com.label.건', '건')}
              </span>
              {selectedOrgTpCd && <div className="info primary">{selectedOrgTpCdNm}</div>}
            </SubTitleGroup>
            <ControlBtnGroup>
              {hasAuth && (
                <>
                  <Button
                    css={IconButton.button}
                    className="addRow"
                    onClick={handleAddRow}
                    disableRipple
                  >
                    {t('com.button.행추가', '행추가')}
                  </Button>
                  <Button
                    css={IconButton.button}
                    className="delRow"
                    onClick={handleDelRow}
                    disableRipple
                  >
                    {t('com.button.행삭제', '행삭제')}
                  </Button>
                </>
              )}
              <Button
                css={IconButton.button}
                className="Exceldown"
                onClick={handleDownloadExcel}
                disableRipple
              >
                {t('com.button.다운로드', '화면 다운로드')}
              </Button>
              <Button
                css={IconButton.button}
                className="refresh"
                onClick={() => handleSearch()}
                disableRipple
              >
                {t('com.button.새로고침', '새로고침')}
              </Button>
            </ControlBtnGroup>
          </SubTitleLayout>
          <CustomGrid
            layoutDefinition={layoutDefinition}
            rowData={filterRowData}
            height={635}
            isReadOnly={!hasAuth}
            initialized={onInitialized}
            beginningEdit={onBeginningEdit}
            cellEditEnding={onCellEditEnding}
          />
        </div>
      </LSmRLgContetntLayout>
      {hasAuth && (
        <GlobalBtnGroup>
          <Button css={IconButton.button} className="save" onClick={() => handleSave()}>
            {t('com.label.저장', '저장')}
          </Button>
        </GlobalBtnGroup>
      )}
    </>
  );
};

export default SetupOrgEqclPage;
